import type { AutosaveStatus } from '@dashboard/modules/settings/composables/useAutosave'
import api from '@dashboard/services/axiosApi'
import { useAuthStore } from '@dashboard/stores/authStore'
import { ref, watch, type Ref } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'

/** Personal settings saved through `PATCH /api/user/settings`. */
export type UserSettings = {
  weekly_emails_enabled: boolean
  optimization_emails_enabled: boolean
  theme: ThemePreference | null
  live_updates_enabled: boolean
  first_promoter_ref_id: string | null
}

// The newest request that sent each key. Only its response may copy that key
// back, so a slow older response can't undo a newer choice.
const newestRequestFor = new Map<string, number>()
let requestCount = 0

/**
 * Saves only the keys given, then copies the saved values of those keys onto
 * the signed-in user so every reader (footer theme toggle, live updates,
 * Notifications) agrees without refetching `/api/user`.
 */
export async function saveUserSettings(changes: Partial<UserSettings>): Promise<UserSettings> {
  const request = ++requestCount
  for (const key of Object.keys(changes)) {
    newestRequestFor.set(key, request)
  }
  const { data } = await api.patch<{ settings: UserSettings }>('/api/user/settings', changes)
  const authStore = useAuthStore()
  if (authStore.user) {
    const settled = Object.entries(data.settings).filter(([key]) => newestRequestFor.get(key) === request)
    authStore.user = { ...authStore.user, ...Object.fromEntries(settled) }
  }
  return data.settings
}

type SwitchSetting = 'weekly_emails_enabled' | 'optimization_emails_enabled' | 'live_updates_enabled'

/**
 * A switch for one saved setting. It keeps the person's choice while their
 * own save is pending or failed, and otherwise follows the signed-in user, so
 * a save from an earlier visit that lands after the page opened still shows.
 */
export function useSettingSwitch(key: SwitchSetting, status: Readonly<Ref<AutosaveStatus>>): Ref<boolean> {
  const authStore = useAuthStore()
  const saved = () => authStore.user?.[key] !== false
  const value = ref(saved())
  watch(saved, (next) => {
    if (status.value !== 'saving' && status.value !== 'error') {
      value.value = next
    }
  })
  return value
}
